import { useCallback, useState } from 'react';

export type DateFilter = 'all' | 'today' | '7d' | '30d';
export type ConversationStatusFilter = 'active' | 'archived';

export interface HistoryFilters {
  date: DateFilter;
  /** Case id to filter on, or null for all dossiers. */
  caseId: string | null;
  /** Personal organization projection displayed by the history panel. */
  status: ConversationStatusFilter;
}

const DEFAULTS: HistoryFilters = { date: 'all', caseId: null, status: 'active' };

export interface UseHistoryFiltersReturn {
  filters: HistoryFilters;
  setDate: (next: DateFilter) => void;
  setCase: (next: string | null) => void;
  setStatus: (next: ConversationStatusFilter) => void;
  reset: () => void;
  /** True when at least one dimension differs from the default. */
  isFiltered: boolean;
}

/**
 * Ephemeral history filters (dossier, archived, date) — not persisted, so each
 * new session of the sidebar starts on the active, unfiltered view.
 */
export function useHistoryFilters(): UseHistoryFiltersReturn {
  const [filters, setFilters] = useState<HistoryFilters>(DEFAULTS);

  const setDate = useCallback(
    (next: DateFilter) => setFilters((prev) => ({ ...prev, date: next })),
    []
  );
  const setCase = useCallback(
    (next: string | null) => setFilters((prev) => ({ ...prev, caseId: next })),
    []
  );
  const setStatus = useCallback(
    (next: ConversationStatusFilter) => setFilters((prev) => ({ ...prev, status: next })),
    []
  );
  const reset = useCallback(() => setFilters(DEFAULTS), []);

  const isFiltered =
    filters.date !== 'all'
    || filters.caseId !== null
    || filters.status !== 'active';

  return { filters, setDate, setCase, setStatus, reset, isFiltered };
}

/**
 * Returns true if `iso` is within the period covered by `dateFilter`.
 * Caller should pre-compute `now` once per render to keep the function pure.
 */
export function matchesDateFilter(iso: string | null | undefined, dateFilter: DateFilter, now: Date = new Date()): boolean {
  if (dateFilter === 'all') return true;
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;

  if (dateFilter === 'today') {
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return t >= startOfDay;
  }
  const days = dateFilter === '7d' ? 7 : 30;
  const threshold = now.getTime() - days * 24 * 60 * 60 * 1000;
  return t >= threshold;
}
